import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Indent,
  Outdent,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
  Upload,
  Video,
  Table as TableIcon,
  Minus,
  Sparkles,
  Eraser,
  Code2,
  Maximize2,
  Minimize2,
  Undo,
  Redo,
  ChevronDown,
  Palette,
  Highlighter,
  Check,
  HelpCircle,
  Eye,
  Type,
  Smile,
  Quote,
  Heading1,
  Heading2,
  Heading3,
  Trash2,
  Move,
  Maximize,
  Sliders,
  Square,
  Circle,
  X,
  Edit3
} from 'lucide-react';

const FONT_FAMILIES = [
  { id: 'jameel', name: 'جمیل نوری نستعلیق (Urdu Nastaliq)', value: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif" },
  { id: 'cairo', name: 'خوبصورت خط سرخی (Cairo / Arabic)', value: "'Cairo', 'Alexandria', sans-serif" },
  { id: 'naskh', name: 'خط نسخ و قرآنی (Noto Sans Arabic)', value: "'Noto Sans Arabic', 'Segoe UI', Tahoma, sans-serif" },
  { id: 'system', name: 'سلیس اردو / سسٹم فونٹ (Segoe UI / Arial)', value: "'Segoe UI', Tahoma, Arial, sans-serif" },
  { id: 'serif', name: 'انگریزی سیرف (Times New Roman / Georgia)', value: "Georgia, 'Times New Roman', serif" },
  { id: 'sans', name: 'انگریزی ماڈرن سانز (Plus Jakarta / Inter)', value: "'Plus Jakarta Sans', system-ui, sans-serif" },
  { id: 'mono', name: 'کوڈ و مونو سپیس (Monospace / Courier)', value: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },
];

const FONT_SIZES = [
  { label: 'باریک (12px)', value: '12px', execSize: '1' },
  { label: 'چھوٹا (14px)', value: '14px', execSize: '2' },
  { label: 'نارمل متن (16px)', value: '16px', execSize: '3' },
  { label: 'درمیانہ (18px)', value: '18px', execSize: '4' },
  { label: 'بڑا (22px)', value: '22px', execSize: '5' },
  { label: 'بہت بڑا (28px)', value: '28px', execSize: '6' },
  { label: 'جلی سرخی (34px)', value: '34px', execSize: '7' },
];

const COLOR_PALETTE = [
  { name: 'سیاہ (Black)', hex: '#111827' },
  { name: 'گہرا سرمئی (Dark Gray)', hex: '#374151' },
  { name: 'سرمئی (Gray)', hex: '#6b7280' },
  { name: 'پنسار زمردی سبز (Emerald)', hex: '#047857' },
  { name: 'گہرا ہربل سبز (Forest Green)', hex: '#1e3a24' },
  { name: 'شاہی نیلا (Royal Blue)', hex: '#1d4ed8' },
  { name: 'آسمانی نیلا (Sky Blue)', hex: '#0284c7' },
  { name: 'یاقوتی سرخ (Ruby Red)', hex: '#dc2626' },
  { name: 'گلابی (Rose)', hex: '#e11d48' },
  { name: 'امبر / سنہری (Amber Gold)', hex: '#d97706' },
  { name: 'گہرا نارنجی (Orange)', hex: '#ea580c' },
  { name: 'جامنی / بنفشی (Purple)', hex: '#7c3aed' },
  { name: 'سفید (White)', hex: '#ffffff' },
];

const HIGHLIGHT_PALETTE = [
  { name: 'کوئی نہیں (None)', hex: 'transparent' },
  { name: 'ہلکا پیلا (Soft Yellow)', hex: '#fef08a' },
  { name: 'ہلکا سبز (Soft Green)', hex: '#bbf7d0' },
  { name: 'ہلکا نیلا (Soft Blue)', hex: '#bae6fd' },
  { name: 'ہلکا گلابی (Soft Pink)', hex: '#fbcfe8' },
  { name: 'ہلکا نارنجی (Soft Orange)', hex: '#fed7aa' },
  { name: 'ہلکا جامنی (Soft Purple)', hex: '#e9d5ff' },
  { name: 'ہلکا سرمئی (Soft Gray)', hex: '#f3f4f6' },
];

const ISLAMIC_SYMBOLS = [
  { symbol: 'ﷺ', label: 'صلی اللہ علیہ وسلم' },
  { symbol: 'ﷻ', label: 'جل جلالہ' },
  { symbol: 'ؑ', label: 'علیہ السلام' },
  { symbol: 'ؓ', label: 'رضی اللہ عنہ' },
  { symbol: 'ؒ', label: 'رحمۃ اللہ علیہ' },
  { symbol: '﷽', label: 'بسم اللہ الرحمن الرحیم' },
  { symbol: '« »', label: 'عربی واوین' },
  { symbol: '﴿ ﴾', label: 'قرآنی بریکٹ' },
  { symbol: '؁', label: 'سنہ / سال' },
  { symbol: '؃', label: 'صفحہ نمبر' },
  { symbol: '٪', label: 'فیصد علامت' },
  { symbol: '🌿', label: 'ہربل پتہ' },
  { symbol: '🍯', label: 'شہد' },
  { symbol: '💊', label: 'دوا / کیپسول' },
  { symbol: '⚠️', label: 'وارننگ / احتیاط' },
  { symbol: '💡', label: 'آئیڈیا / نکتہ' },
  { symbol: '⭐', label: 'ستارہ' },
  { symbol: '✅', label: 'صحیح چیک' },
];

export default function RichWysiwygEditor({
  value = '',
  onChange,
  placeholder = 'یہاں مضمون کا تفصیلی متن لکھیں۔ اوپر دیے گئے ٹولز سے سرخیاں، فونٹ سائز، تصاویر، لنکس اور ٹیبل وغیرہ شامل کریں...',
  minHeight = '480px',
  isCodeMode = false,
  onToggleCodeMode,
}) {
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const editorRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showSymbolsMenu, setShowSymbolsMenu] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);

  const [customColor, setCustomColor] = useState('#047857');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [linkNewTab, setLinkNewTab] = useState(true);
  const [videoUrl, setVideoUrl] = useState('');
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [tableHasHeader, setTableHasHeader] = useState(true);
  const [isUploadingImg, setIsUploadingImg] = useState(false);

  // Selected Image Interactive State (WordPress Image Toolbar & Resizer)
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgOverlayPos, setImgOverlayPos] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [showCaptionModal, setShowCaptionModal] = useState(false);
  const [imageCaptionText, setImageCaptionText] = useState('');

  // Stats
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [readingTime, setReadingTime] = useState(1);

  // Keep editor content in sync with external value
  useEffect(() => {
    if (editorRef.current && !isCodeMode) {
      if (editorRef.current.innerHTML !== (value || '')) {
        editorRef.current.innerHTML = value || '';
        updateStats(editorRef.current.innerText || '');
      }
    }
  }, [value, isCodeMode]);

  // Calculate stats
  const updateStats = (text) => {
    const cleanText = text.trim();
    if (!cleanText) {
      setWordCount(0);
      setCharCount(0);
      setReadingTime(1);
      return;
    }
    const words = cleanText.split(/\s+/).filter(Boolean);
    const chars = cleanText.length;
    const wCount = words.length;
    setWordCount(wCount);
    setCharCount(chars);
    setReadingTime(Math.max(1, Math.ceil(wCount / 180)));
  };

  // Update selected image overlay position
  const updateImageOverlayPosition = useCallback(() => {
    if (!selectedImg || !canvasContainerRef.current || !editorRef.current) {
      setImgOverlayPos(null);
      return;
    }

    // Check if the selected image is still part of the DOM
    if (!editorRef.current.contains(selectedImg)) {
      setSelectedImg(null);
      setImgOverlayPos(null);
      return;
    }

    const canvasRect = canvasContainerRef.current.getBoundingClientRect();
    const imgRect = selectedImg.getBoundingClientRect();

    setImgOverlayPos({
      top: imgRect.top - canvasRect.top,
      left: imgRect.left - canvasRect.left,
      width: imgRect.width,
      height: imgRect.height,
      clientWidth: selectedImg.clientWidth || imgRect.width,
      clientHeight: selectedImg.clientHeight || imgRect.height,
    });
  }, [selectedImg]);

  // Listen to window resize & scroll to update overlay coordinates
  useEffect(() => {
    if (selectedImg) {
      updateImageOverlayPosition();
      const handleScrollOrResize = () => updateImageOverlayPosition();
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }
  }, [selectedImg, updateImageOverlayPosition]);

  // Handle click on images inside visual editor
  const handleEditorClick = (e) => {
    const target = e.target;
    if (target && target.tagName === 'IMG') {
      e.stopPropagation();
      setSelectedImg(target);
      // Find caption if exists
      const parentContainer = target.closest('.article-img-container') || target.parentElement;
      const captionElem = parentContainer ? parentContainer.querySelector('.article-img-caption, p') : null;
      setImageCaptionText(captionElem ? captionElem.innerText : '');
    } else {
      // If clicked somewhere else not inside the image control overlay
      if (!e.target.closest('.image-control-overlay')) {
        setSelectedImg(null);
      }
    }
  };

  // Handle global key events (Delete / Backspace / Escape for selected image)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedImg) {
        if (e.key === 'Escape') {
          setSelectedImg(null);
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
          // If not typing in an input/textarea
          if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
            e.preventDefault();
            handleDeleteSelectedImage();
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImg]);

  // Delete selected image (or its surrounding wrapper)
  const handleDeleteSelectedImage = () => {
    if (!selectedImg) return;
    const parent = selectedImg.closest('.article-img-container, div.my-6, figure') || selectedImg.parentElement;
    if (parent && parent !== editorRef.current && parent.childNodes.length <= 3) {
      parent.remove();
    } else {
      selectedImg.remove();
    }
    setSelectedImg(null);
    setImgOverlayPos(null);
    triggerChange();
  };

  // Apply image alignment
  const handleAlignImage = (align) => {
    if (!selectedImg) return;
    selectedImg.style.display = 'block';
    selectedImg.style.maxWidth = '100%';

    if (align === 'center') {
      selectedImg.style.float = 'none';
      selectedImg.style.margin = '1.5rem auto';
      selectedImg.style.clear = 'both';
    } else if (align === 'right') {
      selectedImg.style.float = 'right';
      selectedImg.style.margin = '0.5rem 0 1rem 1.5rem';
      selectedImg.style.clear = 'none';
    } else if (align === 'left') {
      selectedImg.style.float = 'left';
      selectedImg.style.margin = '0.5rem 1.5rem 1rem 0';
      selectedImg.style.clear = 'none';
    } else if (align === 'full') {
      selectedImg.style.float = 'none';
      selectedImg.style.width = '100%';
      selectedImg.style.margin = '1.5rem auto';
      selectedImg.style.clear = 'both';
    }

    triggerChange();
    setTimeout(updateImageOverlayPosition, 50);
  };

  // Apply quick image width percentage
  const handleSetImageWidthPercent = (percent) => {
    if (!selectedImg) return;
    selectedImg.style.width = `${percent}%`;
    selectedImg.style.height = 'auto';
    selectedImg.style.maxWidth = '100%';
    triggerChange();
    setTimeout(updateImageOverlayPosition, 50);
  };

  // Apply image rounded corners
  const handleSetImageRounded = (radius) => {
    if (!selectedImg) return;
    selectedImg.style.borderRadius = radius;
    triggerChange();
    setTimeout(updateImageOverlayPosition, 50);
  };

  // Drag to Resize Image (Corner Handles)
  const handleStartResize = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedImg) return;

    setIsResizing(true);
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = selectedImg.getBoundingClientRect().width;
    const startHeight = selectedImg.getBoundingClientRect().height;
    const aspectRatio = startWidth / (startHeight || 1);
    const containerWidth = editorRef.current ? editorRef.current.clientWidth - 40 : 800;

    const handleMouseMove = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      let newWidth = startWidth;

      if (direction === 'bottom-right' || direction === 'top-right') {
        newWidth = startWidth + dx;
      } else if (direction === 'bottom-left' || direction === 'top-left') {
        newWidth = startWidth - dx;
      }

      // Constrain size
      newWidth = Math.max(60, Math.min(containerWidth, newWidth));
      selectedImg.style.width = `${Math.round(newWidth)}px`;
      selectedImg.style.height = 'auto';
      updateImageOverlayPosition();
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      triggerChange();
      setTimeout(updateImageOverlayPosition, 50);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Edit or Add Caption to Image
  const handleSaveCaption = (e) => {
    e?.preventDefault();
    setShowCaptionModal(false);
    if (!selectedImg) return;

    let parent = selectedImg.closest('.article-img-container, div.my-6, figure');
    if (!parent || parent === editorRef.current) {
      // Wrap image in container
      const wrapper = document.createElement('div');
      wrapper.className = 'article-img-container my-6 text-center';
      selectedImg.parentNode.insertBefore(wrapper, selectedImg);
      wrapper.appendChild(selectedImg);
      parent = wrapper;
    }

    let captionElem = parent.querySelector('.article-img-caption');
    if (!captionElem) {
      captionElem = document.createElement('p');
      captionElem.className = 'article-img-caption text-xs text-gray-500 font-urdu mt-2';
      parent.appendChild(captionElem);
    }
    captionElem.innerText = imageCaptionText;
    triggerChange();
    setTimeout(updateImageOverlayPosition, 50);
  };

  // Save current text selection (needed when clicking outside modals/dropdowns)
  const saveSelection = () => {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel.getRangeAt && sel.rangeCount) {
        savedSelectionRef.current = sel.getRangeAt(0);
      }
    }
  };

  const restoreSelection = () => {
    if (savedSelectionRef.current && window.getSelection) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  // Generic execCommand with selection preservation & state trigger
  const execCmd = (command, val = null) => {
    if (isCodeMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      document.execCommand(command, false, val);
      triggerChange();
    }
  };

  const triggerChange = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      if (onChange) onChange(html);
      updateStats(editorRef.current.innerText || '');
    }
  };

  // Custom Font Size Applier
  const handleApplyFontSize = (sizePx) => {
    saveSelection();
    if (isCodeMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      const selection = window.getSelection();
      if (!selection.rangeCount || selection.isCollapsed) {
        document.execCommand('fontSize', false, '3');
        return;
      }
      const range = selection.getRangeAt(0);
      const selectedText = range.extractContents();
      const span = document.createElement('span');
      span.style.fontSize = sizePx;
      span.appendChild(selectedText);
      range.insertNode(span);
      selection.removeAllRanges();
      triggerChange();
    }
  };

  // Custom Font Family Applier
  const handleApplyFontFamily = (fontFamilyVal) => {
    saveSelection();
    if (isCodeMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      const selection = window.getSelection();
      if (!selection.rangeCount || selection.isCollapsed) {
        document.execCommand('fontName', false, fontFamilyVal);
        return;
      }
      const range = selection.getRangeAt(0);
      const selectedText = range.extractContents();
      const span = document.createElement('span');
      span.style.fontFamily = fontFamilyVal;
      span.appendChild(selectedText);
      range.insertNode(span);
      selection.removeAllRanges();
      triggerChange();
    }
  };

  // Apply Direction (RTL / LTR)
  const handleApplyDirection = (dir) => {
    saveSelection();
    if (isCodeMode) return;
    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        let node = selection.anchorNode;
        while (node && node !== editorRef.current && node.nodeType !== 1) {
          node = node.parentNode;
        }
        if (node && node !== editorRef.current) {
          node.setAttribute('dir', dir);
          node.style.textAlign = dir === 'rtl' ? 'right' : 'left';
          triggerChange();
          return;
        }
      }
      editorRef.current.setAttribute('dir', dir);
      editorRef.current.style.textAlign = dir === 'rtl' ? 'right' : 'left';
      triggerChange();
    }
  };

  // Insert Custom HTML Helper
  const insertHTML = (htmlContent) => {
    if (isCodeMode) {
      if (textareaRef.current) {
        const start = textareaRef.current.selectionStart;
        const end = textareaRef.current.selectionEnd;
        const text = textareaRef.current.value;
        const newText = text.substring(0, start) + htmlContent + text.substring(end);
        if (onChange) onChange(newText);
      }
      return;
    }

    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      document.execCommand('insertHTML', false, htmlContent);
      triggerChange();
    }
  };

  // Apply Text Color
  const handleApplyTextColor = (colorHex) => {
    setShowColorPicker(false);
    execCmd('foreColor', colorHex);
  };

  // Apply Highlight Background Color
  const handleApplyHighlight = (colorHex) => {
    setShowHighlightPicker(false);
    if (colorHex === 'transparent') {
      execCmd('removeFormat');
    } else {
      execCmd('hiliteColor', colorHex);
    }
  };

  // Open Link Modal
  const openLinkModal = () => {
    saveSelection();
    const sel = window.getSelection();
    setLinkText(sel ? sel.toString() : '');
    setLinkUrl('https://');
    setShowLinkModal(true);
  };

  // Submit Link
  const handleInsertLink = (e) => {
    e?.preventDefault();
    setShowLinkModal(false);
    if (!linkUrl || linkUrl === 'https://') return;

    if (editorRef.current) {
      editorRef.current.focus();
      restoreSelection();
      const targetAttr = linkNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
      const textToUse = linkText || linkUrl;
      const linkHtml = `<a href="${linkUrl}"${targetAttr} class="text-emerald-700 underline font-semibold hover:text-emerald-900">${textToUse}</a>`;
      insertHTML(linkHtml);
    }
  };

  // Insert Table
  const handleInsertTable = () => {
    setShowTableModal(false);
    let tableHtml = `
      <div class="my-6 overflow-x-auto">
        <table class="w-full border-collapse rounded-2xl overflow-hidden shadow-xs border border-emerald-200 text-sm font-urdu">
    `;

    if (tableHasHeader) {
      tableHtml += `<thead class="bg-emerald-800 text-white font-bold"><tr>`;
      for (let c = 1; c <= tableCols; c++) {
        tableHtml += `<th class="p-3 border border-emerald-700 text-right">عنوان ${c}</th>`;
      }
      tableHtml += `</tr></thead>`;
    }

    tableHtml += `<tbody>`;
    for (let r = 1; r <= tableRows; r++) {
      const bgClass = r % 2 === 0 ? 'bg-emerald-50/40' : 'bg-white';
      tableHtml += `<tr class="${bgClass}">`;
      for (let c = 1; c <= tableCols; c++) {
        tableHtml += `<td class="p-3 border border-emerald-100 text-gray-800 text-right">معلومات ${r}-${c}</td>`;
      }
      tableHtml += `</tr>`;
    }
    tableHtml += `</tbody></table></div><p><br></p>`;

    insertHTML(tableHtml);
  };

  // Insert Embedded YouTube Video
  const handleInsertVideo = () => {
    setShowVideoModal(false);
    if (!videoUrl) return;

    let embedUrl = videoUrl;
    const ytMatch = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    if (ytMatch && ytMatch[1]) {
      embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
    }

    const videoHtml = `
      <div class="my-6 text-center">
        <div class="relative w-full aspect-video max-w-2xl mx-auto rounded-3xl overflow-hidden shadow-lg border border-gray-200 bg-black">
          <iframe 
            src="${embedUrl}" 
            title="طبی و معلوماتی ویڈیو" 
            class="absolute inset-0 w-full h-full border-0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen
          ></iframe>
        </div>
        <p class="text-xs text-gray-500 font-urdu mt-2">ویڈیو: وضاحتی رہنمائی</p>
      </div><p><br></p>
    `;

    insertHTML(videoHtml);
    setVideoUrl('');
  };

  // Handle In-Editor Device Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingImg(true);

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width;
        let h = img.height;
        const maxWidth = 1000;
        if (w > maxWidth) {
          h = Math.round((h * maxWidth) / w);
          w = maxWidth;
        }
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        const compressed = canvas.toDataURL('image/webp', 0.85);

        const imgHtml = `
          <div class="article-img-container my-6 text-center">
            <img src="${compressed}" alt="طبی معلوماتی تصویر" style="max-height: 480px; width: auto; margin: 0 auto; border-radius: 1.25rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; display: block; cursor: pointer;" />
            <p class="article-img-caption text-xs text-gray-500 font-urdu mt-2">تصویر کا عنوان / وضاحت</p>
          </div><p><br></p>
        `;
        insertHTML(imgHtml);
        setIsUploadingImg(false);
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Insert Image via URL Prompt
  const handleInsertImageUrl = () => {
    saveSelection();
    const url = prompt('تصویر کا انٹرنیٹ لنک (URL) درج کریں:', 'https://');
    if (url && url !== 'https://') {
      const imgHtml = `
        <div class="article-img-container my-6 text-center">
          <img src="${url}" alt="طبی معلوماتی تصویر" style="max-height: 480px; width: auto; margin: 0 auto; border-radius: 1.25rem; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); border: 1px solid #e5e7eb; display: block; cursor: pointer;" />
          <p class="article-img-caption text-xs text-gray-500 font-urdu mt-2">تصویر کا عنوان / وضاحت</p>
        </div><p><br></p>
      `;
      insertHTML(imgHtml);
    }
  };

  // Insert Callout Cards
  const insertCalloutCard = (type) => {
    let calloutHtml = '';
    if (type === 'tibbi') {
      calloutHtml = `
        <div class="my-5 p-5 rounded-2xl bg-emerald-50/90 border-r-4 border-emerald-600 text-emerald-950 font-urdu shadow-2xs">
          <h4 class="font-bold text-emerald-900 mb-1.5 text-base flex items-center gap-1.5">
            <span>🌿</span> <span>طبی نکتہ و اصولی رہنمائی:</span>
          </h4>
          <p class="text-sm leading-relaxed text-emerald-900">یہاں اپنا مستند طبی نکتہ، مزاج اور عضو کے افعال کے مطابق اصولی رہنمائی درج کریں...</p>
        </div><p><br></p>
      `;
    } else if (type === 'nuskha') {
      calloutHtml = `
        <div class="my-5 p-5 rounded-2xl bg-amber-50/90 border-r-4 border-amber-600 text-amber-950 font-urdu shadow-2xs">
          <h4 class="font-bold text-amber-900 mb-1.5 text-base flex items-center gap-1.5">
            <span>🍯</span> <span>مجرب نسخہ و ترکیب استعمال:</span>
          </h4>
          <p class="text-sm leading-relaxed text-amber-900 font-bold mb-1">اجزاء نسخہ:</p>
          <ul class="list-disc list-inside text-sm text-amber-950 space-y-1 mr-2">
            <li>جزو اول: 10 گرام</li>
            <li>جزو دوم: 20 گرام</li>
          </ul>
          <p class="text-xs text-amber-800 mt-2"><strong>طریقہ استعمال:</strong> صبح و شام پانی کے ساتھ استعمال کریں۔</p>
        </div><p><br></p>
      `;
    } else if (type === 'precaution') {
      calloutHtml = `
        <div class="my-5 p-5 rounded-2xl bg-rose-50/90 border-r-4 border-rose-600 text-rose-950 font-urdu shadow-2xs">
          <h4 class="font-bold text-rose-900 mb-1.5 text-base flex items-center gap-1.5">
            <span>⚠️</span> <span>ضروری احتیاط و پرہیز:</span>
          </h4>
          <p class="text-sm leading-relaxed text-rose-900">مریض کے لیے نقصان دہ غذا، پرہیز اور ضروری انتباہ یہاں درج کریں تاکہ محفوظ علاج ممکن ہو...</p>
        </div><p><br></p>
      `;
    } else if (type === 'note') {
      calloutHtml = `
        <div class="my-5 p-5 rounded-2xl bg-blue-50/90 border-r-4 border-blue-600 text-blue-950 font-urdu shadow-2xs">
          <h4 class="font-bold text-blue-900 mb-1.5 text-base flex items-center gap-1.5">
            <span>💡</span> <span>اہم معلوماتی نوٹ:</span>
          </h4>
          <p class="text-sm leading-relaxed text-blue-900">ضروری ہدایات، حوالہ کتب یا طبی تحقیق کا خلاصہ یہاں لکھیں...</p>
        </div><p><br></p>
      `;
    } else if (type === 'quote') {
      calloutHtml = `
        <blockquote class="my-5 p-5 rounded-2xl bg-slate-100/90 border-r-4 border-slate-700 text-slate-900 font-urdu italic text-base leading-relaxed shadow-2xs">
          « غذا ہی کو اپنی دوا بناؤ اور دوا کو اپنی غذا۔ »
          <span class="block text-xs font-bold text-slate-500 mt-2 text-left dir-ltr">— بقراط (Hippocrates)</span>
        </blockquote><p><br></p>
      `;
    }
    insertHTML(calloutHtml);
  };

  // Insert Special Symbol
  const handleInsertSymbol = (sym) => {
    setShowSymbolsMenu(false);
    insertHTML(`&nbsp;${sym}&nbsp;`);
  };

  return (
    <div 
      ref={containerRef}
      className={`relative border border-gray-300 rounded-3xl shadow-sm bg-white transition-all ${isFullscreen ? 'fixed inset-0 z-50 rounded-none overflow-y-auto' : ''}`}
    >
      
      {/* Hidden File Input for In-Editor Images */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImageUpload}
        accept="image/*"
        className="hidden"
      />

      {/* ===================================================================== */}
      {/* WORDPRESS / GUTENBERG STYLE MULTI-TIER TOOLBAR */}
      {/* ===================================================================== */}
      <div className="sticky top-0 z-30 bg-slate-50 border-b border-gray-200 p-2 sm:p-2.5 rounded-t-3xl shadow-2xs space-y-1.5" dir="rtl">
        
        {/* ROW 1: PRIMARY FORMATTING & TYPOGRAPHY */}
        <div className="flex flex-wrap items-center justify-between gap-1.5">
          
          <div className="flex flex-wrap items-center gap-1">
            
            {/* Undo / Redo */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5">
              <button
                type="button"
                onClick={() => execCmd('undo')}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700 hover:text-pansar-800 transition cursor-pointer"
                title="پہلے جیسا کریں (Undo - Ctrl+Z)"
              >
                <Undo className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => execCmd('redo')}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-700 hover:text-pansar-800 transition cursor-pointer"
                title="دوبارہ لاگو کریں (Redo - Ctrl+Y)"
              >
                <Redo className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Paragraph / Headings Formats */}
            <select
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'blockquote') {
                  execCmd('formatBlock', '<blockquote>');
                } else if (val === 'pre') {
                  execCmd('formatBlock', '<pre>');
                } else if (val) {
                  execCmd('formatBlock', `<${val}>`);
                }
                e.target.value = '';
              }}
              defaultValue=""
              className="px-2.5 py-1.5 rounded-xl border border-gray-200 text-xs font-urdu font-bold bg-white text-gray-800 shadow-2xs focus:outline-none cursor-pointer hover:border-emerald-500"
              title="پیراگراف یا سرخی کی قسم منتخب کریں"
            >
              <option value="" disabled>🏷️ فارمیٹ / سرخی</option>
              <option value="p">پیراگراف (Normal Text)</option>
              <option value="h1">سرخی 1 - جلی عنوان (H1)</option>
              <option value="h2">سرخی 2 - بڑی سرخی (H2)</option>
              <option value="h3">سرخی 3 - درمیانی سرخی (H3)</option>
              <option value="h4">سرخی 4 - چھوٹی سرخی (H4)</option>
              <option value="blockquote">اقتباس (Quote)</option>
              <option value="pre">کوڈ بلاک (Code / Pre)</option>
            </select>

            {/* Font Family Selector (خط و فونٹ) */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyFontFamily(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
              className="px-2.5 py-1.5 rounded-xl border border-gray-200 text-xs font-urdu font-bold bg-white text-gray-800 shadow-2xs focus:outline-none cursor-pointer hover:border-emerald-500"
              title="تحریر کا خط و فونٹ منتخب کریں"
            >
              <option value="" disabled>✒️ خط و فونٹ (Font)</option>
              {FONT_FAMILIES.map(f => (
                <option key={f.id} value={f.value}>{f.name}</option>
              ))}
            </select>

            {/* Font Size Selector (سائز) */}
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleApplyFontSize(e.target.value);
                  e.target.value = '';
                }
              }}
              defaultValue=""
              className="px-2.5 py-1.5 rounded-xl border border-gray-200 text-xs font-urdu font-bold bg-white text-gray-800 shadow-2xs focus:outline-none cursor-pointer hover:border-emerald-500"
              title="فونٹ کا سائز تبدیل کریں"
            >
              <option value="" disabled>📏 فونٹ سائز (Size)</option>
              {FONT_SIZES.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>

            {/* Typography Styles: Bold, Italic, Underline, Strike, Sub, Super */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5">
              <button
                type="button"
                onClick={() => execCmd('bold')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer font-bold"
                title="بولڈ / جلی (Ctrl+B)"
              >
                <Bold className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('italic')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer italic"
                title="ترچھا / اٹیلک (Ctrl+I)"
              >
                <Italic className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('underline')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer underline"
                title="انڈر لائن (Ctrl+U)"
              >
                <Underline className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('strikeThrough')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="خط کشیدہ / سٹرائیک تھرو"
              >
                <Strikethrough className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('subscript')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="سب سکرپٹ (X₂)"
              >
                <Subscript className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('superscript')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="سپر سکرپٹ (X²)"
              >
                <Superscript className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Colors Popovers (Text Color & Highlight) */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  saveSelection();
                  setShowColorPicker(!showColorPicker);
                  setShowHighlightPicker(false);
                }}
                className="px-2 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-emerald-50 text-xs font-urdu font-bold text-gray-800 shadow-2xs flex items-center gap-1 cursor-pointer transition"
                title="متن کا رنگ (Text Color)"
              >
                <Palette className="w-3.5 h-3.5 text-emerald-700" />
                <span className="hidden sm:inline">رنگ</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showColorPicker && (
                <div className="absolute top-full mt-1.5 right-0 z-50 bg-white rounded-2xl border border-gray-200 shadow-xl p-3 w-56 space-y-2.5 animate-fade-in">
                  <span className="text-[11px] font-bold text-gray-700 font-urdu block">متن کا رنگ منتخب کریں:</span>
                  <div className="grid grid-cols-6 gap-1.5">
                    {COLOR_PALETTE.map((c) => (
                      <button
                        key={c.hex}
                        type="button"
                        onClick={() => handleApplyTextColor(c.hex)}
                        className="w-6 h-6 rounded-full border border-gray-300 shadow-xs hover:scale-110 transition cursor-pointer"
                        style={{ backgroundColor: c.hex }}
                        title={c.name}
                      />
                    ))}
                  </div>
                  <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-2">
                    <span className="text-[10px] text-gray-500 font-urdu">کسٹم کلر:</span>
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => {
                        setCustomColor(e.target.value);
                        handleApplyTextColor(e.target.value);
                      }}
                      className="w-8 h-7 rounded cursor-pointer border-0 p-0"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Highlight Background Color Popover */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  saveSelection();
                  setShowHighlightPicker(!showHighlightPicker);
                  setShowColorPicker(false);
                }}
                className="px-2 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-amber-50 text-xs font-urdu font-bold text-gray-800 shadow-2xs flex items-center gap-1 cursor-pointer transition"
                title="متن کا ہائی لائٹ بیک گراؤنڈ رنگ"
              >
                <Highlighter className="w-3.5 h-3.5 text-amber-600" />
                <span className="hidden sm:inline">ہائی لائٹ</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showHighlightPicker && (
                <div className="absolute top-full mt-1.5 right-0 z-50 bg-white rounded-2xl border border-gray-200 shadow-xl p-3 w-56 space-y-2.5 animate-fade-in">
                  <span className="text-[11px] font-bold text-gray-700 font-urdu block">ہائی لائٹ رنگ منتخب کریں:</span>
                  <div className="grid grid-cols-4 gap-1.5">
                    {HIGHLIGHT_PALETTE.map((h) => (
                      <button
                        key={h.hex}
                        type="button"
                        onClick={() => handleApplyHighlight(h.hex)}
                        className="h-7 rounded-lg border border-gray-300 shadow-xs hover:scale-105 transition cursor-pointer text-[10px] font-urdu font-bold text-gray-800 flex items-center justify-center"
                        style={{ backgroundColor: h.hex === 'transparent' ? '#ffffff' : h.hex }}
                        title={h.name}
                      >
                        {h.hex === 'transparent' ? 'مٹائیں' : ''}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>

          {/* Right Mode Actions: Fullscreen & Code View */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => execCmd('removeFormat')}
              className="p-1.5 rounded-xl border border-gray-200 bg-white hover:bg-rose-50 text-gray-600 hover:text-rose-700 transition cursor-pointer shadow-2xs"
              title="فارمیٹنگ ختم کریں (Clear Formatting)"
            >
              <Eraser className="w-3.5 h-3.5" />
            </button>

            {onToggleCodeMode && (
              <button
                type="button"
                onClick={onToggleCodeMode}
                className={`px-2.5 py-1.5 rounded-xl text-xs font-urdu font-bold flex items-center gap-1 cursor-pointer transition shadow-2xs ${
                  isCodeMode ? 'bg-pansar-900 text-white' : 'bg-white hover:bg-gray-100 text-gray-800 border border-gray-200'
                }`}
                title="ایچ ٹی ایم ایل کوڈ اور بصری موڈ تبدیل کریں"
              >
                <Code2 className="w-3.5 h-3.5 text-emerald-600" />
                <span className="hidden sm:inline">{isCodeMode ? 'واپس بصری موڈ' : 'کوڈ دیکھیں'}</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 text-gray-700 transition cursor-pointer shadow-2xs"
              title={isFullscreen ? 'فل سکرین بند کریں' : 'فل سکرین فوکس موڈ (Distraction-Free)'}
            >
              {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
            </button>
          </div>

        </div>

        {/* ROW 2: ALIGNMENT, DIRECTION, LISTS, LINKS, MEDIA & TABLES */}
        <div className="flex flex-wrap items-center justify-between gap-1.5 pt-1 border-t border-gray-200/60">
          
          <div className="flex flex-wrap items-center gap-1">
            
            {/* Text Alignments */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5">
              <button
                type="button"
                onClick={() => execCmd('justifyRight')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="دائیں جانب سیدھ (Align Right)"
              >
                <AlignRight className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('justifyCenter')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="درمیان میں سیدھ (Align Center)"
              >
                <AlignCenter className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('justifyLeft')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="بائیں جانب سیدھ (Align Left)"
              >
                <AlignLeft className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('justifyFull')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="دونوں طرف برابر / جسٹیفائی (Justify Full)"
              >
                <AlignJustify className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Direction: RTL vs LTR */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5 text-xs font-urdu font-bold">
              <button
                type="button"
                onClick={() => handleApplyDirection('rtl')}
                className="px-2 py-1 rounded-lg hover:bg-emerald-50 text-emerald-900 transition cursor-pointer"
                title="دائیں سے بائیں سمت (RTL - اردو/عربی)"
              >
                RTL (دائیں)
              </button>
              <button
                type="button"
                onClick={() => handleApplyDirection('ltr')}
                className="px-2 py-1 rounded-lg hover:bg-blue-50 text-blue-900 transition cursor-pointer"
                title="بائیں سے دائیں سمت (LTR - انگریزی/کوڈ)"
              >
                LTR (بائیں)
              </button>
            </div>

            {/* Lists & Indentations */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5">
              <button
                type="button"
                onClick={() => execCmd('insertUnorderedList')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="نکتہ وار فہرست (Bullet List)"
              >
                <List className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('insertOrderedList')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="نمبر وار فہرست (Numbered List)"
              >
                <ListOrdered className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('indent')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="حاشیہ آگے بڑھائیں (Increase Indent)"
              >
                <Indent className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('outdent')}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-gray-800 hover:text-emerald-800 transition cursor-pointer"
                title="حاشیہ پیچھے کریں (Decrease Indent)"
              >
                <Outdent className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Links */}
            <div className="flex items-center bg-white rounded-xl border border-gray-200 shadow-2xs p-0.5">
              <button
                type="button"
                onClick={openLinkModal}
                className="p-1.5 rounded-lg hover:bg-emerald-50 text-emerald-800 transition cursor-pointer"
                title="ویب سائٹ لنک شامل کریں (Insert Link)"
              >
                <LinkIcon className="w-3.5 h-3.5" />
              </button>

              <button
                type="button"
                onClick={() => execCmd('unlink')}
                className="p-1.5 rounded-lg hover:bg-rose-50 text-rose-700 transition cursor-pointer"
                title="لنک ختم کریں (Remove Link)"
              >
                <Unlink className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Horizontal Line Divider */}
            <button
              type="button"
              onClick={() => insertHTML('<hr class="my-6 border-t-2 border-emerald-100" /><p><br></p>')}
              className="p-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-100 text-gray-700 transition cursor-pointer shadow-2xs"
              title="خط فاصل / افقی لکیر (Horizontal Divider)"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>

            {/* Table Generator */}
            <button
              type="button"
              onClick={() => {
                saveSelection();
                setShowTableModal(true);
              }}
              className="px-2 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-emerald-50 text-xs font-urdu font-bold text-gray-800 shadow-2xs flex items-center gap-1 cursor-pointer transition"
              title="جدول داخل کریں (Insert Table)"
            >
              <TableIcon className="w-3.5 h-3.5 text-emerald-700" />
              <span>جدول (Table)</span>
            </button>

            {/* Video Embed */}
            <button
              type="button"
              onClick={() => {
                saveSelection();
                setShowVideoModal(true);
              }}
              className="px-2 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-red-50 text-xs font-urdu font-bold text-gray-800 shadow-2xs flex items-center gap-1 cursor-pointer transition"
              title="یوٹیوب یا ویڈیو ایمبیڈ کریں (Embed Video)"
            >
              <Video className="w-3.5 h-3.5 text-red-600" />
              <span>ویڈیو</span>
            </button>

            {/* Media Upload Buttons */}
            <button
              type="button"
              disabled={isUploadingImg}
              onClick={() => fileInputRef.current?.click()}
              className="px-2.5 py-1.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
              title="کمپیوٹر یا موبائل سے تصویر داخل کریں"
            >
              <Upload className="w-3.5 h-3.5" />
              <span>{isUploadingImg ? 'اپلوڈ جاری...' : 'تصویر اپلوڈ'}</span>
            </button>

            <button
              type="button"
              onClick={handleInsertImageUrl}
              className="px-2 py-1.5 rounded-xl bg-white hover:bg-gray-100 text-gray-800 border border-gray-200 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
              title="انٹرنیٹ لنک سے تصویر داخل کریں"
            >
              <ImageIcon className="w-3.5 h-3.5 text-pansar-700" />
              <span>تصویر URL</span>
            </button>

            {/* Islamic Symbols & Marks */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  saveSelection();
                  setShowSymbolsMenu(!showSymbolsMenu);
                }}
                className="px-2 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-amber-50 text-xs font-urdu font-bold text-gray-800 shadow-2xs flex items-center gap-1 cursor-pointer transition"
                title="رموز، علامات و ایموجیز"
              >
                <Smile className="w-3.5 h-3.5 text-amber-600" />
                <span>رموز و علامات</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {showSymbolsMenu && (
                <div className="absolute top-full mt-1.5 right-0 z-50 bg-white rounded-2xl border border-gray-200 shadow-xl p-3 w-64 space-y-2 animate-fade-in">
                  <span className="text-[11px] font-bold text-gray-700 font-urdu block">خصوصی علامات و اوقاف:</span>
                  <div className="grid grid-cols-6 gap-1.5">
                    {ISLAMIC_SYMBOLS.map((s, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handleInsertSymbol(s.symbol)}
                        className="p-1.5 rounded-lg border border-gray-200 hover:bg-emerald-50 hover:border-emerald-400 text-xs font-urdu font-bold text-gray-900 transition flex items-center justify-center cursor-pointer shadow-2xs"
                        title={s.label}
                      >
                        {s.symbol}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

          </div>

        </div>

        {/* ROW 3: TIBBI & SPECIAL CALLOUT CARDS */}
        <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-gray-200/60">
          <span className="text-[11px] font-bold text-gray-600 font-urdu ml-1">خصوصی باکسز:</span>

          <button
            type="button"
            onClick={() => insertCalloutCard('tibbi')}
            className="px-2.5 py-1 rounded-xl bg-emerald-100/90 text-emerald-900 hover:bg-emerald-200 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
            title="طبی نکتہ و اصولی رہنمائی باکس"
          >
            <span>🌿</span>
            <span>طبی نکتہ باکس</span>
          </button>

          <button
            type="button"
            onClick={() => insertCalloutCard('nuskha')}
            className="px-2.5 py-1 rounded-xl bg-amber-100/90 text-amber-950 hover:bg-amber-200 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
            title="مجرب نسخہ و ترکیب استعمال باکس"
          >
            <span>🍯</span>
            <span>مجرب نسخہ باکس</span>
          </button>

          <button
            type="button"
            onClick={() => insertCalloutCard('precaution')}
            className="px-2.5 py-1 rounded-xl bg-rose-100/90 text-rose-950 hover:bg-rose-200 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
            title="احتیاطی تدبیر و پرہیز باکس"
          >
            <span>⚠️</span>
            <span>احتیاط و پرہیز باکس</span>
          </button>

          <button
            type="button"
            onClick={() => insertCalloutCard('note')}
            className="px-2.5 py-1 rounded-xl bg-blue-100/90 text-blue-950 hover:bg-blue-200 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
            title="معلوماتی نوٹ باکس"
          >
            <span>💡</span>
            <span>اہم نوٹ باکس</span>
          </button>

          <button
            type="button"
            onClick={() => insertCalloutCard('quote')}
            className="px-2.5 py-1 rounded-xl bg-slate-200/90 text-slate-800 hover:bg-slate-300 text-xs font-bold font-urdu flex items-center gap-1 cursor-pointer shadow-2xs transition"
            title="سنہری اقوال و اقتباس باکس"
          >
            <span>📜</span>
            <span>اقتباس / قول</span>
          </button>
        </div>

      </div>

      {/* ===================================================================== */}
      {/* MAIN WRITING CANVAS (VISUAL WYSIWYG OR RAW HTML) */}
      {/* ===================================================================== */}
      <div ref={canvasContainerRef} className="relative">
        {!isCodeMode ? (
          <div
            ref={editorRef}
            contentEditable
            dir="rtl"
            onClick={handleEditorClick}
            onInput={() => {
              triggerChange();
              if (selectedImg) updateImageOverlayPosition();
            }}
            onBlur={triggerChange}
            onKeyUp={(e) => {
              saveSelection();
              if (selectedImg) updateImageOverlayPosition();
            }}
            onMouseUp={saveSelection}
            className="p-6 sm:p-8 bg-white focus:outline-none text-gray-800 font-urdu leading-loose text-base space-y-4 focus:ring-2 focus:ring-emerald-500/20 rounded-b-3xl"
            style={{ minHeight, direction: 'rtl', textAlign: 'right' }}
            data-placeholder={placeholder}
          />
        ) : (
          <textarea
            ref={textareaRef}
            rows={22}
            value={value || ''}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              updateStats(e.target.value);
            }}
            placeholder="HTML Source Code..."
            className="w-full p-5 text-xs font-mono text-emerald-400 leading-relaxed focus:outline-none bg-gray-900 rounded-b-3xl"
            dir="ltr"
            style={{ minHeight }}
          />
        )}

        {/* ===================================================================== */}
        {/* INTERACTIVE WORDPRESS-STYLE SELECTED IMAGE OVERLAY & RESIZE HANDLES */}
        {/* ===================================================================== */}
        {!isCodeMode && selectedImg && imgOverlayPos && (
          <div
            className="image-control-overlay absolute pointer-events-none z-40 transition-opacity"
            style={{
              top: `${imgOverlayPos.top}px`,
              left: `${imgOverlayPos.left}px`,
              width: `${imgOverlayPos.width}px`,
              height: `${imgOverlayPos.height}px`,
            }}
          >
            {/* Active Highlight Border */}
            <div className="absolute inset-0 border-2 border-emerald-600 rounded-xl pointer-events-none ring-4 ring-emerald-500/20 shadow-lg" />

            {/* 4 Corner Resize Drag Handles */}
            <div
              onMouseDown={(e) => handleStartResize(e, 'top-left')}
              className="absolute -top-2 -left-2 w-4 h-4 bg-emerald-700 border-2 border-white rounded-full shadow-md cursor-nwse-resize pointer-events-auto hover:scale-125 transition-transform"
              title="تصویر کا سائز چھوٹا یا بڑا کریں (ڈریگ کریں)"
            />
            <div
              onMouseDown={(e) => handleStartResize(e, 'top-right')}
              className="absolute -top-2 -right-2 w-4 h-4 bg-emerald-700 border-2 border-white rounded-full shadow-md cursor-nesw-resize pointer-events-auto hover:scale-125 transition-transform"
              title="تصویر کا سائز چھوٹا یا بڑا کریں (ڈریگ کریں)"
            />
            <div
              onMouseDown={(e) => handleStartResize(e, 'bottom-left')}
              className="absolute -bottom-2 -left-2 w-4 h-4 bg-emerald-700 border-2 border-white rounded-full shadow-md cursor-nesw-resize pointer-events-auto hover:scale-125 transition-transform"
              title="تصویر کا سائز چھوٹا یا بڑا کریں (ڈریگ کریں)"
            />
            <div
              onMouseDown={(e) => handleStartResize(e, 'bottom-right')}
              className="absolute -bottom-2 -right-2 w-4 h-4 bg-emerald-700 border-2 border-white rounded-full shadow-md cursor-nwse-resize pointer-events-auto hover:scale-125 transition-transform"
              title="تصویر کا سائز چھوٹا یا بڑا کریں (ڈریگ کریں)"
            />

            {/* Floating WordPress Image Quick Actions Toolbar */}
            <div
              className="absolute -top-14 left-1/2 -translate-x-1/2 pointer-events-auto bg-gray-900/95 backdrop-blur-md text-white p-1.5 rounded-2xl shadow-2xl border border-gray-700/80 flex items-center gap-1 text-xs font-urdu whitespace-nowrap animate-fade-in z-50"
              dir="rtl"
            >
              {/* Delete Button */}
              <button
                type="button"
                onClick={handleDeleteSelectedImage}
                className="p-1.5 rounded-xl bg-rose-600/90 hover:bg-rose-600 text-white font-bold flex items-center gap-1 transition cursor-pointer shadow-xs"
                title="تصویر ڈیلیٹ کریں (Delete Image)"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span className="text-[11px]">ڈیلیٹ</span>
              </button>

              <span className="h-4 w-px bg-gray-700 mx-0.5" />

              {/* Alignment Controls */}
              <div className="flex items-center bg-gray-800/90 rounded-xl p-0.5">
                <button
                  type="button"
                  onClick={() => handleAlignImage('right')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-200 hover:text-white transition cursor-pointer"
                  title="دائیں سیدھ (Align Right)"
                >
                  <AlignRight className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleAlignImage('center')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-200 hover:text-white transition cursor-pointer"
                  title="درمیان (Align Center)"
                >
                  <AlignCenter className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleAlignImage('left')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-200 hover:text-white transition cursor-pointer"
                  title="بائیں سیدھ (Align Left)"
                >
                  <AlignLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleAlignImage('full')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-200 hover:text-white transition cursor-pointer"
                  title="پوری چوڑائی (Full Width 100%)"
                >
                  <Maximize className="w-3.5 h-3.5" />
                </button>
              </div>

              <span className="h-4 w-px bg-gray-700 mx-0.5" />

              {/* Size Presets */}
              <div className="flex items-center bg-gray-800/90 rounded-xl p-0.5 text-[11px] font-sans font-bold">
                <button
                  type="button"
                  onClick={() => handleSetImageWidthPercent(25)}
                  className="px-1.5 py-1 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="25% چوڑائی"
                >
                  25%
                </button>
                <button
                  type="button"
                  onClick={() => handleSetImageWidthPercent(50)}
                  className="px-1.5 py-1 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="50% چوڑائی"
                >
                  50%
                </button>
                <button
                  type="button"
                  onClick={() => handleSetImageWidthPercent(75)}
                  className="px-1.5 py-1 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="75% چوڑائی"
                >
                  75%
                </button>
                <button
                  type="button"
                  onClick={() => handleSetImageWidthPercent(100)}
                  className="px-1.5 py-1 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="100% مکمل چوڑائی"
                >
                  100%
                </button>
              </div>

              <span className="h-4 w-px bg-gray-700 mx-0.5" />

              {/* Rounded Corners */}
              <div className="flex items-center bg-gray-800/90 rounded-xl p-0.5">
                <button
                  type="button"
                  onClick={() => handleSetImageRounded('0px')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="سیدھے کونے (Square)"
                >
                  <Square className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => handleSetImageRounded('1.25rem')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="گول کونے (Rounded)"
                >
                  <span className="text-[10px] font-bold">گول</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSetImageRounded('9999px')}
                  className="p-1.5 rounded-lg hover:bg-gray-700 text-gray-300 hover:text-white transition cursor-pointer"
                  title="دائرہ (Circle)"
                >
                  <Circle className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* Edit Caption Button */}
              <button
                type="button"
                onClick={() => setShowCaptionModal(true)}
                className="p-1.5 rounded-xl hover:bg-gray-800 text-gray-300 hover:text-white transition cursor-pointer flex items-center gap-1 text-[11px]"
                title="کیپشن / عنوان تبدیل کریں"
              >
                <Edit3 className="w-3.5 h-3.5 text-amber-400" />
                <span className="hidden sm:inline">کیپشن</span>
              </button>

              {/* Close Selection */}
              <button
                type="button"
                onClick={() => setSelectedImg(null)}
                className="p-1.5 rounded-xl hover:bg-gray-800 text-gray-400 hover:text-white transition cursor-pointer"
                title="سلیکشن ختم کریں (Escape)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Current Size Label Pill */}
            <div className="absolute bottom-2 left-2 bg-black/80 text-white text-[10px] font-mono px-2 py-0.5 rounded-md pointer-events-none shadow">
              {Math.round(imgOverlayPos.width)} × {Math.round(imgOverlayPos.height)} px
            </div>
          </div>
        )}

      </div>

      {/* ===================================================================== */}
      {/* BOTTOM FOOTER STATUS & LIVE STATS BAR */}
      {/* ===================================================================== */}
      <div className="bg-slate-50 border-t border-gray-200 px-4 py-2 rounded-b-3xl flex flex-wrap items-center justify-between text-xs text-gray-500 font-urdu gap-2" dir="rtl">
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <strong>الفاظ:</strong> {wordCount}
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1">
            <strong>حروف:</strong> {charCount}
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1">
            <strong>پڑھنے کا وقت:</strong> تقریباً {readingTime} منٹ
          </span>
        </div>

        <div className="flex items-center gap-2 text-[11px] text-emerald-800 font-medium">
          <span>💡 تصویر پر کلک کر کے کونوں سے پکڑ کر چھوٹا/بڑا کریں یا ڈیلیٹ کریں۔</span>
        </div>
      </div>

      {/* ===================================================================== */}
      {/* MODAL: EDIT IMAGE CAPTION */}
      {/* ===================================================================== */}
      {showCaptionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs" dir="rtl">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-gray-200 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 font-urdu text-base flex items-center gap-2">
                <Edit3 className="w-4 h-4 text-amber-600" />
                <span>تصویر کا کیپشن / وضاحتی عنوان درج کریں</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowCaptionModal(false)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveCaption} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  تصویر کا عنوان / کیپشن (Caption)
                </label>
                <input
                  type="text"
                  value={imageCaptionText}
                  onChange={(e) => setImageCaptionText(e.target.value)}
                  placeholder="مثلاً: کلونجی کے بیج اور طبی خواص"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCaptionModal(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  منسوخ
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-urdu font-bold shadow-xs cursor-pointer"
                >
                  محفوظ کریں
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: INSERT LINK */}
      {/* ===================================================================== */}
      {showLinkModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs" dir="rtl">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-gray-200 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 font-urdu text-base flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-emerald-700" />
                <span>لنک داخل کریں (Insert Link)</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowLinkModal(false)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleInsertLink} className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  ویب سائٹ کا لنک (URL) *
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-sans dir-ltr text-left focus:outline-none focus:border-emerald-600"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  متن / ٹیکسٹ جو نظر آئے گا (Anchor Text)
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="مثلاً: یہاں کلک کریں"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-urdu focus:outline-none focus:border-emerald-600"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="newTabCheckbox"
                  checked={linkNewTab}
                  onChange={(e) => setLinkNewTab(e.target.checked)}
                  className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
                <label htmlFor="newTabCheckbox" className="text-xs font-urdu text-gray-700 cursor-pointer">
                  نئے ٹیب میں کھولیں (Open link in new tab)
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowLinkModal(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  منسوخ
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-urdu font-bold shadow-xs cursor-pointer"
                >
                  لنک لگائیں
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: INSERT TABLE */}
      {/* ===================================================================== */}
      {showTableModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs" dir="rtl">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-gray-200 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 font-urdu text-base flex items-center gap-2">
                <TableIcon className="w-4 h-4 text-emerald-700" />
                <span>جدول / ٹیبل بنائیں (Insert Table)</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowTableModal(false)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                    سطریں (Rows)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={tableRows}
                    onChange={(e) => setTableRows(parseInt(e.target.value) || 1)}
                    className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-sm font-sans focus:outline-none focus:border-emerald-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                    کالمز (Columns)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={tableCols}
                    onChange={(e) => setTableCols(parseInt(e.target.value) || 1)}
                    className="w-full px-3.5 py-2 rounded-xl border border-gray-300 text-sm font-sans focus:outline-none focus:border-emerald-600"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="hasHeaderCheckbox"
                  checked={tableHasHeader}
                  onChange={(e) => setTableHasHeader(e.target.checked)}
                  className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                />
                <label htmlFor="hasHeaderCheckbox" className="text-xs font-urdu text-gray-700 cursor-pointer">
                  اوپری ہیڈر سطر شامل کریں (Include Header Row)
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowTableModal(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  منسوخ
                </button>
                <button
                  type="button"
                  onClick={handleInsertTable}
                  className="px-5 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-urdu font-bold shadow-xs cursor-pointer"
                >
                  جدول شامل کریں
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===================================================================== */}
      {/* MODAL: EMBED VIDEO */}
      {/* ===================================================================== */}
      {showVideoModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-xs" dir="rtl">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full border border-gray-200 shadow-2xl space-y-4 animate-fade-in">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 font-urdu text-base flex items-center gap-2">
                <Video className="w-4 h-4 text-red-600" />
                <span>یوٹیوب ویڈیو ایمبیڈ کریں (Embed Video)</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowVideoModal(false)}
                className="text-gray-400 hover:text-gray-700 cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-bold text-gray-700 font-urdu mb-1">
                  یوٹیوب ویڈیو کا لنک (YouTube URL) *
                </label>
                <input
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-gray-300 text-xs font-sans dir-ltr text-left focus:outline-none focus:border-red-600"
                />
                <span className="text-[10px] text-gray-400 font-urdu mt-1 block">
                  یوٹیوب ویڈیو کا کوئی بھی لنک درج کریں، یہ خود بخود رسپانسیو پلیئر بنا دے گا۔
                </span>
              </div>

              <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowVideoModal(false)}
                  className="px-4 py-2 rounded-xl border border-gray-200 text-xs font-urdu font-bold text-gray-600 hover:bg-gray-100 cursor-pointer"
                >
                  منسوخ
                </button>
                <button
                  type="button"
                  onClick={handleInsertVideo}
                  className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-urdu font-bold shadow-xs cursor-pointer"
                >
                  ویڈیو داخل کریں
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
